import { Project } from "../../../shared/interfaces/project";
import { getAuthHeaders } from "../../../shared/services/authentication";
import { BASE_URL } from "../../../shared/utils/variables";

const baseURL = BASE_URL;

// Buscar projetos agrupados por investigador principal
export const fetchProjectsGroupedService = async (search?: string) => {
    const options = {
        method: 'GET',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json" 
        }
    };  

    const searchTerm = search || '';

    try {
        const response = await fetch(`${baseURL}/projectGroupByPrincipalInvestigator?search=${encodeURIComponent(searchTerm)}`, options);
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Erro HTTP ${response.status}`);
        }

        return response.json();
    } catch (error) {
        throw new Error(`Erro ao buscar projetos: ${error}`);
    }
};

export const fetchProjectsService = async (search?: string, companyId?: string) => {
    const options = {
        method: 'GET',
        headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json"
        }
    };

    const searchTerm = search || '';
    const params = new URLSearchParams();

    params.append('search', searchTerm);

    if (companyId) {
        params.append('companyId', companyId);
    }

    try {
        const response = await fetch(`${baseURL}/projects?${params.toString()}`, options);

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Erro HTTP ${response.status}`);
        }

        return response.json();
    } catch (error) {
        throw new Error(`Erro ao buscar projetos: ${error}`);
    }
};

// Buscar projeto específico por ID
export const fetchProjectService = async (id: string): Promise<Project> => {
    const url = `${baseURL}/project/${id}`;

    const options = {
        method: 'GET',
        headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

// Criar novo projeto
export const createProjectService = async (data: Project) => {
    const url = `${baseURL}/project`;
    
    const options = {
        method: 'POST',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

// Editar projeto existente
export const fetchEditProjectService = async (id: string, data: Project) => {
    const url = `${baseURL}/project/${id}`;
    
    const options = {
        method: 'PUT',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

// Deletar projeto
export const deleteProjectService = async (id: string) => {
    const url = `${baseURL}/project/${id}`;
    
    const options = {
        method: 'DELETE',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

// Finalizar projeto
export const finishedProjectService = async (id: string) => {
    const url = `${baseURL}/project-finished/${id}`;
    
    const options = {
        method: 'PUT',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

export const fetchAssignableProjectsService = async (companyId?: string) => {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
    const url = `${baseURL}/projects/assignable${query}`;

    const options = {
        method: 'GET',
        headers: { 
            ...getAuthHeaders(),
            "Content-Type": "application/json",
        },
    };

    const response = await fetch(url, options);

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro HTTP ${response.status}`);
    }

    return response.json();
};

// Exportar como default para facilitar importação
export default {
    fetchProjectsGroupedService,
    fetchProjectsService,
    fetchProjectService,
    createProjectService,
    fetchEditProjectService,
    deleteProjectService,
    finishedProjectService,
    fetchAssignableProjectsService
};
